<template>
  <div
    v-if="completedWorkflowTransitionLabel || canAdvanceWorkflow"
    class="workflow-proceed-buttons"
    @click.stop
  >
    <LISAUIButton
      v-if="completedWorkflowTransitionLabel"
      variant="secondary"
      disabled
      icon="fas fa-check"
      outlined
      :size="buttonSize"
    >
      {{ completedWorkflowTransitionLabel }}
    </LISAUIButton>
    <LISASplitButton
      v-else-if="transitions.length > 1"
      :items="transitionMenuItems"
      variant="success"
      :size="buttonSize"
      outlined
      icon="fas fa-arrow-right"
      :menu-aria-label="workflowMenuAriaLabel"
      :disabled="workflowButtonDisabled"
      :title="getWorkflowTransitionTooltip(transitions[0])"
    />
    <LISAUIButton
      v-else
      variant="success"
      :size="buttonSize"
      icon="fas fa-arrow-right"
      outlined
      :disabled="workflowButtonDisabled"
      :title="getWorkflowTransitionTooltip(transitions[0])"
      @click="requestWorkflowAdvance(transitions[0].id)"
    >
      {{ transitions[0].label }}
    </LISAUIButton>
  </div>

  <SQDCWorkflowTransitionRequirementsModal
    v-model:visible="transitionRequirementsVisible"
    :requires-comment="pendingTransition?.requiresComment === true"
    :datapoints="workflowDatapointCandidates"
    @proceed="proceedWithWorkflowRequirements"
  />

  <SQDCEventActionModalComponent
    v-if="pendingModalTransition != null"
    :source="{
      kind: 'workflow-transition',
      workflowInstanceId,
      workflowStepId,
      connectionId: pendingModalTransition.id,
      node: pendingModalTransition.targetNode,
      eventId: event.id,
      eventOwnerId: event.creatorId,
      transitionComment: pendingModalTransition.transitionComment,
      selectedDatapointId: pendingModalTransition.selectedDatapointId,
    }"
    :site-id="siteId"
    :board-id="boardId"
    @close="closePendingModalTransition"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useTranslation } from "@/language/translation";
import { useApolloClient } from "@vue/apollo-composable";
import {
  LISAButton as LISAUIButton,
  LISASplitButton,
  type LISAMenuItem,
} from "@fi-solutions/lisa-ui";
import {
  EABLoadBoardEventDocument,
  useSQDCWorkflowStepCompleteMutation,
  type EABLoadBoardEventsQuery,
} from "@/graphql";
import { useSQDCWorkflowRuntime } from "../../workflow/useSQDCWorkflowRuntime";
import { useSQDCWorkflowAdvanceLock } from "../../workflow/useSQDCWorkflowAdvanceLock";
import {
  getActiveSQDCWorkflowSteps,
  resolveSQDCWorkflowText,
  type SQDCWorkflowConnection,
  type SQDCWorkflowDefinition,
} from "../../workflow/domain";
import SQDCWorkflowTransitionRequirementsModal, {
  type SQDCWorkflowDatapointCandidate,
} from "../workflow/SQDCWorkflowTransitionRequirementsModal.vue";
import SQDCEventActionModalComponent from "./SQDCEventActionModalComponent.vue";

type EABEvent = EABLoadBoardEventsQuery["SQDCEvents"][number];
type RuntimeTransition = SQDCWorkflowConnection & {
  targetNode?: SQDCWorkflowDefinition["nodes"][number];
  waitsForParallelJoin?: boolean;
  label: string;
};
type PendingModalTransition = RuntimeTransition & {
  targetNode: SQDCWorkflowDefinition["nodes"][number];
  transitionComment?: string;
  selectedDatapointId?: number;
};

const props = defineProps<{
  workflowInstanceId: string;
  workflowStepId: string;
  event: EABEvent;
  siteId: number;
  boardId?: number;
  disabled?: boolean;
  small?: boolean;
}>();

const { t, locale } = useTranslation();
const { client: apolloClient } = useApolloClient();
const transitionRequirementsVisible = ref(false);
const pendingTransitionId = ref<string>();
const pendingModalTransition = ref<PendingModalTransition>();
const advanceRequested = ref(false);
const locallyCompletedTransitionLabel = ref("");
const {
  pending: workflowAdvancePending,
  run: runWorkflowAdvanceOnce,
  reset: resetWorkflowAdvance,
} = useSQDCWorkflowAdvanceLock();
const { mutate: completeWorkflowStep } = useSQDCWorkflowStepCompleteMutation();
const {
  instance: workflowInstance,
  step: workflowStep,
  transitions: runtimeTransitions,
  refetchInstances,
} = useSQDCWorkflowRuntime(
  () => props.workflowInstanceId,
  () => props.workflowStepId,
);

const transitions = computed<RuntimeTransition[]>(() =>
  runtimeTransitions.value.map((transition) => ({
    ...transition,
    label: getWorkflowTransitionLabel(transition),
  })),
);
const canAdvanceWorkflow = computed(
  () =>
    workflowInstance.value != null &&
    workflowStep.value != null &&
    getActiveSQDCWorkflowSteps(workflowInstance.value).some(
      (step) => step.workflowStepId === props.workflowStepId,
    ) &&
    transitions.value.length > 0,
);
const completedWorkflowTransitionLabel = computed(() => {
  if (locallyCompletedTransitionLabel.value) return locallyCompletedTransitionLabel.value;
  const step = workflowStep.value;
  if (step == null || !("status" in step) || step.status !== "COMPLETED") return "";
  const storedLabel = resolveSQDCWorkflowText(step.completedButtonLabel, locale.value, t);
  if (storedLabel) return storedLabel;
  return (
    transitions.value.find((transition) => transition.id === step.completedConnectionId)?.label ??
    ""
  );
});
const workflowButtonDisabled = computed(
  () => props.disabled || advanceRequested.value || workflowAdvancePending.value,
);
const buttonSize = computed<"sm" | "md">(() => (props.small ? "sm" : "md"));
const workflowMenuAriaLabel = computed(() =>
  transitions.value.map((transition) => transition.label).join(", "),
);
const pendingTransition = computed(() =>
  transitions.value.find((transition) => transition.id === pendingTransitionId.value),
);
const transitionMenuItems = computed<LISAMenuItem[]>(() =>
  transitions.value.map((transition) => ({
    id: transition.id,
    label: transition.label,
    callback: () => requestWorkflowAdvance(transition.id),
  })),
);
const workflowDatapointCandidates = computed<SQDCWorkflowDatapointCandidate[]>(() =>
  pendingTransition.value?.datapointTypeId == null
    ? []
    : props.event.DatapointEventConnection.map(({ datapoint }) => ({
        id: datapoint.id,
        timestamp: datapoint.timestamp,
        quantity: datapoint.quantity,
        typeName: datapoint.datapointType?.name ?? String(datapoint.typeId),
        costCenterName: datapoint.CostCenter?.name,
      })),
);

function getWorkflowTransitionLabel(transition: RuntimeTransition) {
  if (transition.buttonLabelId != null) return t(`dynamic.${transition.buttonLabelId}`);
  const connectionLabel = resolveSQDCWorkflowText(transition.buttonLabel, locale.value, t);
  const targetLabel =
    transition.targetNode == null
      ? ""
      : t(
          `modules.sqdc.components.workflow.SQDCWorkflowEditor.nodeTypes.${transition.targetNode.type}`,
        );
  const label = connectionLabel || targetLabel;
  return transition.waitsForParallelJoin
    ? `${label} (${t("modules.sqdc.components.workflow.SQDCWorkflowEditor.parallelPrepareSuffix")})`
    : label;
}

function getWorkflowTransitionTooltip(transition?: SQDCWorkflowConnection) {
  return transition?.showDescriptionAsTooltip
    ? resolveSQDCWorkflowText(transition.description, locale.value, t)
    : undefined;
}

function requestWorkflowAdvance(connectionId: string) {
  if (!canAdvanceWorkflow.value || workflowButtonDisabled.value) return;
  const transition = transitions.value.find((candidate) => candidate.id === connectionId);
  if (transition == null) return;
  advanceRequested.value = true;
  const requiresSelection =
    transition.datapointTypeId != null && props.event.DatapointEventConnection.length > 1;
  if (transition.requiresComment === true || requiresSelection) {
    pendingTransitionId.value = connectionId;
    transitionRequirementsVisible.value = true;
    return;
  }
  void runWorkflowAdvance(connectionId, {});
}

function proceedWithWorkflowRequirements(requirements: {
  transitionComment?: string;
  selectedDatapointId?: number;
}) {
  const connectionId = pendingTransitionId.value;
  transitionRequirementsVisible.value = false;
  pendingTransitionId.value = undefined;
  if (connectionId != null) void runWorkflowAdvance(connectionId, requirements);
}

async function runWorkflowAdvance(
  connectionId: string,
  requirements: { transitionComment?: string; selectedDatapointId?: number },
) {
  try {
    const advanced = await runWorkflowAdvanceOnce(async () => {
      const transition = transitions.value.find((candidate) => candidate.id === connectionId);
      if (!canAdvanceWorkflow.value || transition?.targetNode == null) return false;

      if (transition.targetNode.type !== "END" && transition.waitsForParallelJoin !== true) {
        pendingModalTransition.value = {
          ...transition,
          targetNode: transition.targetNode,
          ...requirements,
        };
        return true;
      }

      const result = await completeWorkflowStep({
        input: {
          workflowInstanceId: props.workflowInstanceId,
          workflowStepId: props.workflowStepId,
          connectionId,
          ...requirements,
        },
      });
      if (result?.data?.SQDCWorkflowStepComplete !== true) return false;
      locallyCompletedTransitionLabel.value = transition.label;

      await Promise.all([
        refetchInstances(),
        apolloClient.query({
          query: EABLoadBoardEventDocument,
          variables: { ids: [props.event.id] },
          fetchPolicy: "network-only",
        }),
      ]);
      return true;
    });
    if (!advanced) advanceRequested.value = false;
  } catch (error) {
    advanceRequested.value = false;
    throw error;
  }
}

async function closePendingModalTransition() {
  const completedLabel = pendingModalTransition.value?.label ?? "";
  pendingModalTransition.value = undefined;
  await refetchInstances();
  if (workflowStep.value != null && "status" in workflowStep.value) {
    locallyCompletedTransitionLabel.value =
      workflowStep.value.status === "COMPLETED" ? completedLabel : "";
  }
  advanceRequested.value = locallyCompletedTransitionLabel.value !== "";
  resetWorkflowAdvance();
}

watch(transitionRequirementsVisible, (visible) => {
  if (visible || pendingTransitionId.value == null) return;
  pendingTransitionId.value = undefined;
  advanceRequested.value = false;
});
</script>

<style scoped lang="scss">
.workflow-proceed-buttons {
  display: flex;
  justify-content: flex-end;
  margin-top: 0.5rem;
}
</style>
